import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Package, Search, Check, X, Minus, Plus, LayoutGrid, List, Pencil } from "lucide-react";

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("dc_stock_view") || "list");
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [tempVal, setTempVal] = useState("");
  const [gridModal, setGridModal] = useState(null); // {product}
  const [gridVal, setGridVal] = useState("");

  useEffect(() => { localStorage.setItem("dc_stock_view", view); }, [view]);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/products"), api.get("/categories")])
      .then(([p, c]) => { setProducts(p.data); setCategories(c.data); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStock = async (id, val) => {
    const v = Math.max(0, parseFloat(val) || 0);
    setProducts((list) => list.map((p) => (p.id === id ? { ...p, closing_stock: v } : p)));
    try { await api.put(`/products/${id}`, { closing_stock: v }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const available = products.filter((p) => p.is_available !== false);
  const filtered = available.filter((p) => {
    const inCat = !cat || (p.categories?.includes(cat) || p.category === cat);
    const inSearch = !q || p.name?.toLowerCase().includes(q.toLowerCase());
    return inCat && inSearch;
  });
  const codeMap = {};
  available.forEach((p) => { if (p.product_code != null) codeMap[p.product_code] = p; });

  return (
    <div className="animate-fade-up">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-6 h-6 text-[#8B4513]" />
        <h1 className="text-xl font-bold text-[#8B4513]">Manage Stock</h1>
      </div>

      {/* View toggle */}
      <div className="flex gap-2 mb-3">
        <button onClick={() => setView("grid")} data-testid="grid-view-btn"
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-[#8B4513] font-bold text-sm ${view === "grid" ? "bg-[#8B4513] text-white" : "bg-white text-[#8B4513]"}`}>
          <LayoutGrid className="w-4 h-4" /> Grid View
        </button>
        <button onClick={() => setView("list")} data-testid="list-view-btn"
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-[#8B4513] font-bold text-sm ${view === "list" ? "bg-[#8B4513] text-white" : "bg-white text-[#8B4513]"}`}>
          <List className="w-4 h-4" /> List View
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-14 rounded-xl skeleton" />)}</div>
      ) : view === "grid" ? (
        <div>
          <p className="text-xs text-[#666] mb-2">Product codes 1–100. Tap a box to edit its stock.</p>
          <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5" data-testid="stock-grid">
            {Array.from({ length: 100 }, (_, i) => i + 1).map((code) => {
              const p = codeMap[code];
              const has = !!p;
              const filled = has && (p.closing_stock || 0) > 0;
              return (
                <button key={code} data-testid={`grid-box-${code}`} disabled={!has}
                  onClick={() => { if (has) { setGridModal(p); setGridVal(String(p.closing_stock || 0)); } }}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center border ${!has ? "bg-[#f3efe3] border-[#eee] opacity-50" : filled ? "bg-[#8B4513] border-[#8B4513] text-white" : "bg-white border-[#e6dcc0] text-[#8B4513]"}`}>
                  <span className="text-[11px] font-bold leading-none">{code}</span>
                  {has && <span className="text-sm font-bold leading-none mt-0.5">{(p.closing_stock || 0) > 0 ? p.closing_stock : "-"}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {/* Search + category filter */}
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-[#a99] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." data-testid="stock-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-[#e6dcc0] text-sm focus:outline-none focus:border-[#8B4513]" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
            <button onClick={() => setCat(null)} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${!cat ? "bg-[#8B4513] text-white" : "bg-white border border-[#e6dcc0] text-[#8B4513]"}`}>All</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setCat(cat === c.name ? null : c.name)} data-testid={`cat-${c.name}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${cat === c.name ? "bg-[#8B4513] text-white" : "bg-white border border-[#e6dcc0] text-[#8B4513]"}`}>{c.name}</button>
            ))}
          </div>

          <div className="space-y-2">
            {filtered.length === 0 && <div className="bg-white rounded-xl py-12 text-center text-[#666]">No products found.</div>}
            {filtered.map((p) => (
              <div key={p.id} data-testid={`stock-row-${p.id}`} className="bg-white rounded-xl p-3 flex items-center gap-2 shadow-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#333] text-sm truncate">{p.name} <span className="text-[#8B4513]">- ₹{Number(p.price).toFixed(2)}</span></div>
                </div>
                <button onClick={() => updateStock(p.id, (p.closing_stock || 0) - 1)} className="w-8 h-8 rounded-lg bg-[#FFF8DC] border border-[#e6dcc0] flex items-center justify-center text-[#8B4513]"><Minus className="w-4 h-4" /></button>
                {editId === p.id ? (
                  <>
                    <input autoFocus value={tempVal} onChange={(e) => setTempVal(e.target.value)} type="number" data-testid={`stock-input-${p.id}`}
                      className="w-16 px-2 py-1.5 rounded-lg bg-[#FFF8DC] border border-[#8B4513] text-center font-bold" />
                    <button onClick={() => { updateStock(p.id, tempVal); setEditId(null); }} data-testid={`stock-save-${p.id}`} className="text-[#4CAF50]"><Check className="w-6 h-6" /></button>
                    <button onClick={() => setEditId(null)} className="text-[#f44336]"><X className="w-6 h-6" /></button>
                  </>
                ) : (
                  <button onClick={() => { setEditId(p.id); setTempVal(String(p.closing_stock || 0)); }} data-testid={`stock-value-${p.id}`}
                    className="min-w-[48px] px-2 py-1.5 rounded-lg bg-[#FFF8DC] flex items-center justify-center gap-1 font-bold text-[#333]">
                    {p.closing_stock || 0}<Pencil className="w-3 h-3 text-[#666]" />
                  </button>
                )}
                <button onClick={() => updateStock(p.id, (p.closing_stock || 0) + 1)} className="w-8 h-8 rounded-lg bg-[#FFF8DC] border border-[#e6dcc0] flex items-center justify-center text-[#8B4513]"><Plus className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Grid edit modal */}
      {gridModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setGridModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-xs p-5" onClick={(e) => e.stopPropagation()} data-testid="grid-edit-modal">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-[#8B4513] bg-[#FFF8DC] px-2 py-1 rounded-lg">Code #{gridModal.product_code}</span>
              <button onClick={() => setGridModal(null)}><X className="w-5 h-5 text-[#666]" /></button>
            </div>
            <div className="font-bold text-[#333] mb-3">{gridModal.name}</div>
            <label className="text-sm text-[#666]">Closing stock</label>
            <input type="number" value={gridVal} onChange={(e) => setGridVal(e.target.value)} autoFocus data-testid="grid-stock-input"
              className="w-full mt-1 px-3 py-3 rounded-xl bg-[#FFF8DC] border border-[#8B4513] text-center text-lg font-bold" />
            <button onClick={() => { updateStock(gridModal.id, gridVal); setGridModal(null); }} data-testid="grid-stock-save"
              className="w-full mt-4 py-3 rounded-xl bg-[#8B4513] hover:bg-[#703710] text-white font-bold">Save</button>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
